import { useCallback, useContext } from "react";
import { useSelector } from "react-redux";
import { Location } from "react-router-dom-v5-compat";
import { v4 as uuid } from "uuid";

import { Tag } from "@seneca/senekit";

import useHasAccessToExamQuestions from "seneca-common/features/exam-questions/hooks/useHasAccessToExamQuestions";
import { isSignedIn } from "seneca-common/features/user/state";
import { useSimpleNavigationCallback } from "seneca-common/utils/hooks/routing";

import { makeLinkToCourseExamQuestionsHomepageSelectModeWithState } from "features/classroom/routes/classroom/routes/course/routes/course-exam-questions-homepage/routes/select-mode/links";
import {
  linkToCourseHomepage,
  linkToCourseHomePageSignUp
} from "features/classroom/routes/links";
import { sidebarWillOverlayClassroomWhenOpen } from "features/common/components/sidebar/utils";
import {
  ExamQuestionsSource,
  logClickStartExamQuestions
} from "features/exam-questions/analytics";
import { ROUTES } from "routing/routes";

import useGetNumberOfUnstudiedAssignmentsForCourse from "../../features/assignments/hooks/useGetNumberOfUnstudiedAssignmentsForCourse";
import { useSectionRowQuitSessionLink } from "../../features/common/hooks";
import { SidebarContext } from "../../SidebarContextProvider";
import { SidebarMenuItemLabel } from "../../types";
import SidebarMenuItem from "../SidebarMenuItem/SidebarMenuItem";
import useHasVisitedExamPrep from "./utils/useHasVisitedExampPrep";

type Props = {
  courseId: string;
  activeSectionId?: string;
  toggleSidebar: () => void;
};

export default function SidebarMenu({
  courseId,
  activeSectionId = "",
  toggleSidebar
}: Props) {
  const {
    visibleItems: {
      overview: overviewVisible,
      assignments: assignmentsVisible,
      examPrep: examPrepVisible,
      askAmelia: askAmeliaVisible
    },
    activeItems: {
      overview: studyActive,
      assignments: assignmentsActive,
      examPrep: examPrepActive,
      askAmelia: askAmeliaActive
    }
  } = useContext(SidebarContext);

  const linkToCourseHomePage = useSectionRowQuitSessionLink({
    courseId,
    activeSectionId,
    link: linkToCourseHomepage(courseId)
  });

  const linkToCourseExamQuestions = useSectionRowQuitSessionLink({
    courseId,
    activeSectionId,
    link: makeLinkToCourseExamQuestionsHomepageSelectModeWithState({
      courseId,
      source: ExamQuestionsSource.courseSidebar
    })
  });

  const linkToAssignments = useSectionRowQuitSessionLink({
    courseId,
    activeSectionId,
    link: ROUTES.CLASSROOM.COURSE.ASSIGNMENTS.buildPath({ courseId })
  });

  const linkToAskAmelia = useSectionRowQuitSessionLink({
    courseId,
    activeSectionId,
    link: {
      pathname: ROUTES.CLASSROOM.COURSE.ASK_AMELIA.CHAT.buildPath({
        courseId,
        sessionId: uuid()
      }),
      state: ROUTES.CLASSROOM.COURSE.ASK_AMELIA.CHAT.buildState({
        isNewSession: true
      })
    } as Location
  });

  const navigateToSelectExamQuestionsPage = useSimpleNavigationCallback(
    linkToCourseExamQuestions
  );

  const navigateToAssignmentsPage =
    useSimpleNavigationCallback(linkToAssignments);
  const navigateToAskAmeliaPage = useSimpleNavigationCallback(linkToAskAmelia);
  const navigateToSignUp = useSimpleNavigationCallback(
    linkToCourseHomePageSignUp(courseId, {
      redirectAuthenticatedUserToLocation: linkToCourseHomepage(courseId)
    })
  );

  const signedIn = useSelector(isSignedIn);
  const numUnstudiedUpcomingAssignments =
    useGetNumberOfUnstudiedAssignmentsForCourse();
  const onClickStudy = useSimpleNavigationCallback(linkToCourseHomePage);

  const { examPrepVisitsLimitReached, incrementExamPrepVisits } =
    useHasVisitedExamPrep();

  const onClickExamPrep = useCallback(() => {
    if (!signedIn) {
      navigateToSignUp();
    } else {
      logClickStartExamQuestions();
      incrementExamPrepVisits();
      navigateToSelectExamQuestionsPage();
      if (sidebarWillOverlayClassroomWhenOpen()) toggleSidebar();
    }
  }, [
    signedIn,
    incrementExamPrepVisits,
    navigateToSignUp,
    navigateToSelectExamQuestionsPage,
    toggleSidebar
  ]);

  const onClickAssignments = useCallback(() => {
    navigateToAssignmentsPage();
    if (
      !numUnstudiedUpcomingAssignments &&
      sidebarWillOverlayClassroomWhenOpen()
    )
      toggleSidebar();
  }, [
    navigateToAssignmentsPage,
    numUnstudiedUpcomingAssignments,
    toggleSidebar
  ]);
  const onClickAskAmelia = useCallback(() => {
    navigateToAskAmeliaPage();
    if (sidebarWillOverlayClassroomWhenOpen()) toggleSidebar();
  }, [navigateToAskAmeliaPage, toggleSidebar]);
  const { hasUnlimitedQuestions, remainingQuestionsCount, isLoading } =
    useHasAccessToExamQuestions();

  return (
    <>
      {overviewVisible && (
        <SidebarMenuItem
          label={SidebarMenuItemLabel.overview}
          iconName="play"
          active={studyActive}
          onClick={onClickStudy}
        />
      )}
      {examPrepVisible && (
        <SidebarMenuItem
          label={SidebarMenuItemLabel.examPrep}
          iconName="examQuestion"
          active={examPrepActive}
          onClick={onClickExamPrep}
          tag={
            (hasUnlimitedQuestions === false || !examPrepVisitsLimitReached) &&
            !isLoading ? (
              <Tag
                tone={examPrepVisitsLimitReached ? "warning" : "success"}
                strength="weak"
                size="xsmall"
              >
                {examPrepVisitsLimitReached ? remainingQuestionsCount : "New"}
              </Tag>
            ) : undefined
          }
        />
      )}
      {assignmentsVisible && (
        <SidebarMenuItem
          label={SidebarMenuItemLabel.assignments}
          iconName="books"
          active={assignmentsActive}
          onClick={onClickAssignments}
          tag={
            numUnstudiedUpcomingAssignments ? (
              <Tag tone="warning" strength="weak" size="xsmall">
                {numUnstudiedUpcomingAssignments}
              </Tag>
            ) : undefined
          }
        />
      )}
      {askAmeliaVisible && (
        <SidebarMenuItem
          label={SidebarMenuItemLabel.askAmelia}
          iconName="chat"
          active={askAmeliaActive}
          onClick={onClickAskAmelia}
        />
      )}
    </>
  );
}
